import { useEffect, useState } from 'react';
import type { PublicTextReminderWording } from '@rx-manager/shared';
import { MessageSquare } from 'lucide-react';
import { useI18n } from '../../lib/i18n';
import { CHECKBOX, cn } from '../../components/ui';

/*
 * Pieces the booking flow shares with the waitlist and the offer page (Track BCW): the text
 * consent box, the hold countdown, and the consent text's fingerprint.
 */

/**
 * Track RBA — "Text me appointment reminders at this number", unticked until the
 * patient ticks it, with the words they agree to (reminders, then care messages) in
 * the language on screen. Those are the server's versioned wording, which it records
 * with the yes; the page never writes its own.
 */
export function TextRemindersBox({
  wordings,
  checked,
  onChange,
  label,
}: {
  wordings: PublicTextReminderWording[];
  checked: boolean;
  onChange: (value: boolean) => void;
  /** Track BCW — the waitlist's own words for the box ("Text me when a time opens up"). */
  label?: string;
}) {
  const { t, locale } = useI18n();
  const words = (['reminders', 'service'] as const)
    .map((purpose) => wordings.find((wording) => wording.purpose === purpose)?.text[locale])
    .filter((text): text is string => Boolean(text?.trim()));
  return (
    <div
      className={cn(
        'space-y-3 rounded-xl border p-4 transition-colors',
        checked ? 'border-primary bg-accent/60' : 'border-input',
      )}
    >
      <label htmlFor="text-reminders" className="flex min-h-11 cursor-pointer items-start gap-3">
        <input
          id="text-reminders"
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          aria-describedby={words.length > 0 ? 'text-reminders-wording' : undefined}
          className={CHECKBOX}
        />
        <span className="flex items-start gap-2 text-base font-medium text-heading">
          <MessageSquare className="mt-0.5 h-5 w-5 shrink-0 text-primary-strong" aria-hidden="true" />
          {label ?? t('details.textReminders')}
        </span>
      </label>
      {words.length > 0 && (
        <div id="text-reminders-wording" className="space-y-2 pl-8 text-sm text-muted-foreground">
          <p>{t('details.textRemindersAgree')}</p>
          {/* The server's words, as plain text — never as markup. */}
          {words.map((sentence) => (
            <p key={sentence} className="whitespace-pre-wrap text-foreground">
              {sentence}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Milliseconds left on the hold, or null when there is none.
 *
 * Ticks every five seconds — often enough for the last-minute notice to appear
 * on time, rarely enough that its `aria-live` region doesn't talk over
 * everything else.
 */
export function useHoldCountdown(expiresAt: string | null): number | null {
  const [left, setLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!expiresAt) {
      setLeft(null);
      return;
    }
    const tick = (): void => setLeft(Math.max(Date.parse(expiresAt) - Date.now(), 0));
    tick();
    const timer = setInterval(tick, 5_000);
    return () => clearInterval(timer);
  }, [expiresAt]);

  return left;
}

/**
 * A short, stable fingerprint of the consent text the patient actually saw.
 *
 * Stored with the consent record so it is later possible to say *which* wording
 * was agreed to — the pharmacist can edit the text afterwards, and a bare
 * "consented: true" would then be evidence of nothing.
 */
export function hashOf(text: string): string {
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) | 0;
  }
  return `v${(hash >>> 0).toString(36)}`;
}
